import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials, selectWorkflowId } from '@/selectors/selectors';
import { globalMessage } from '@/utils/globalMessage';
import * as API from '@/api';
import { AgeRatingDeclaration, AppAgeRatingDefault, ageRatingNeedsAttention } from '@/types/ageRating';
import { AgeRatingQuestionnaireModal } from './ageRatingQuestionnaire';
import { WarningOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import styles from '../../styles/settingsStyles';

interface AppAgeRatingDefaultSettingsProps {
    appId: string;
    orgId: string;
}

const ageRatingDefaultCache = new Map<string, AppAgeRatingDefault>();

const AppAgeRatingDefaultSettings: React.FC<AppAgeRatingDefaultSettingsProps> = ({ appId, orgId }) => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));

    const [config, setConfig] = React.useState<AppAgeRatingDefault | undefined>(() =>
        ageRatingDefaultCache.get(workflowId),
    );
    const [loading, setLoading] = React.useState(() => !ageRatingDefaultCache.has(workflowId));
    const [saving, setSaving] = React.useState(false);
    const [modalOpen, setModalOpen] = React.useState(false);
    const questionSet = config?.questionSet;
    const initialValues = config?.declaration ?? config?.ascDeclaration ?? {};

    React.useEffect(() => {
        let cancelled = false;
        if (!auth || !workflowId) {
            return undefined;
        }
        API.getAppAgeRatingDefault(orgId, workflowId, auth)
            .then((result) => {
                ageRatingDefaultCache.set(workflowId, result);
                if (!cancelled) {
                    setConfig(result);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    globalMessage.error('Failed to load age rating default');
                    console.error(e);
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, workflowId, auth]);

    const onSaveAnswers = async (declaration: AgeRatingDeclaration) => {
        if (!auth || !workflowId) {
            return;
        }
        setSaving(true);
        try {
            const updated = await API.updateAppAgeRatingDefault(orgId, workflowId, { declaration }, auth);
            ageRatingDefaultCache.set(workflowId, updated);
            setConfig(updated);
            globalMessage.success('Age rating default saved');
            setModalOpen(false);
        } catch (e) {
            globalMessage.error('Failed to save age rating default');
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Spin style={{ marginTop: 24 }} />;
    }

    // Age rating only applies to App Store Connect — hide the section otherwise.
    if (!config?.hasAppStoreConnectDestination) {
        return null;
    }

    const needsAttention = ageRatingNeedsAttention(
        config?.declaration,
        config?.questionSetVersion,
        questionSet?.version,
    );
    const needsAttentionMessage =
        config?.declaration ? 'Apple added new age rating questions. Review and re-save to stay compliant.'
        : config?.ascDeclaration ? 'No age rating default is set. Existing answers were loaded from App Store Connect.'
        : 'Age rating has not been set up yet.';

    return (
        <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 720 }}>
            <Space direction="vertical" size={4}>
                <Text style={styles.appSettingsSectionTitleStyle}>Age rating</Text>
                <Text type="secondary">
                    Set the default App Store age ratings Runway will apply to relevant destinations in this FlightPath.
                </Text>
            </Space>

            <Space>
                <Button onClick={() => setModalOpen(true)} disabled={!questionSet}>
                    {config?.declaration ? 'Edit age rating defaults' : 'Set up age rating defaults'}
                </Button>
                {needsAttention && (
                    <Text type="warning">
                        <WarningOutlined /> {needsAttentionMessage}
                    </Text>
                )}
            </Space>

            {questionSet && (
                <AgeRatingQuestionnaireModal
                    title="App Store age rating default"
                    open={modalOpen}
                    onCancel={() => setModalOpen(false)}
                    onSave={onSaveAnswers}
                    questionSet={questionSet}
                    initialValues={initialValues}
                    isEditable={true}
                    saving={saving}
                    description={
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            App Store Connect carries existing answers forward to new versions. If needed, the answers
                            below will be applied in App Store Connect for all relevant destinations in this FlightPath.
                        </Text>
                    }
                />
            )}
        </Space>
    );
};

export default AppAgeRatingDefaultSettings;
